import { LOINC_CODES } from '@liza/fhir-utils';
import type { PlanDefinition, PlanDefinitionAction, Timing } from '@medplum/fhirtypes';
import { applicable, delivery, every, itemKind, loincCode, patient, taskCategory } from './builders';
import { CKD_FOCUS_CODINGS } from './ckdOngoingMeasurements';

export const CKD_STAGE_MONITORING_URL = 'https://liza.health/fhir/PlanDefinition/ckd-stage-monitoring';
export const CKD_STAGE_MONITORING_VERSION = '1.0.0';
/** Version-pinned canonical, the value an expanded suggestion records as its provenance. */
export const CKD_STAGE_MONITORING_CANONICAL = `${CKD_STAGE_MONITORING_URL}|${CKD_STAGE_MONITORING_VERSION}`;

const KDIGO_CKD_2024 = {
  title: 'KDIGO 2024 Clinical Practice Guideline for the Evaluation and Management of CKD',
  url: 'https://kdigo.org/guidelines/ckd-evaluation-and-management/',
} as const;

/**
 * The variables this protocol binds before evaluating any condition.
 *
 * Both are the patient's latest value, not a staged category: KDIGO's grid is indexed on GFR and
 * albuminuria, and the engine has no notion of a CKD stage, so the conditions compare the numbers
 * directly and the stage boundaries live in the FHIRPath rather than in a derived variable.
 */
export const CKD_STAGE_CONTEXT_VARIABLES = [
  {
    name: '%egfr',
    description: `Latest Observation with LOINC ${LOINC_CODES.EGFR}, from any source; empty when none.`,
  },
  {
    name: '%uacr',
    description: `Latest Observation with LOINC ${LOINC_CODES.URINE_ALBUMIN_CREATININE_RATIO}, from any source; empty when none.`,
  },
] as const;

export type CkdStageContextVariable = (typeof CKD_STAGE_CONTEXT_VARIABLES)[number]['name'];

/**
 * KDIGO Figure 13, transcribed. The numbers are monitoring frequency in times per year, indexed on
 * GFR category down and albuminuria category across:
 *
 * ```
 *              A1 (<30)  A2 (30-299)  A3 (>=300 mg/g)
 *   G1 (>=90)     1           1            3
 *   G2 (60-89)    1           1            3
 *   G3a (45-59)   1           2            3
 *   G3b (30-44)   2           3            3
 *   G4 (15-29)    3           3           4+
 *   G5 (<15)     4+          4+           4+
 * ```
 *
 * `4+` has no upper bound, so it is encoded as four times a year — the floor of what the guideline
 * asks, consistent with how every other range in these protocols resolves to the end that satisfies
 * it.
 *
 * Every one of the 18 cells carries a frequency, including G1/A1 and G2/A1. Those two are additionally
 * labelled "Screen" rather than "Treat" — they sit in the low-risk band that is not CKD absent another
 * marker of kidney damage — but that is a second axis layered on the grid, not an absence of cadence.
 * A chart reaching this protocol already has a CKD diagnosis on it, so the yearly frequency applies.
 *
 * Transcribed from the 2024 edition specifically. G1/A3 and G2/A3 read 2 in KDIGO 2012 and 3 here:
 * the 2024 figure is reproduced from the 2022 ADA/KDIGO consensus report rather than carried forward,
 * and those are the only two cells whose numbers changed between editions.
 */
const MONITORING_FREQUENCIES = {
  yearly: every(1, 'a'),
  twiceYearly: every(6, 'mo'),
  thriceYearly: every(4, 'mo'),
  fourTimesYearly: every(3, 'mo'),
} as const;

/** The eGFR bounds of each GFR category, as FHIRPath against the latest value. */
const GFR_CATEGORY = {
  g1: '%egfr.value.value >= 90',
  g2: '%egfr.value.value >= 60 and %egfr.value.value < 90',
  g3a: '%egfr.value.value >= 45 and %egfr.value.value < 60',
  g3b: '%egfr.value.value >= 30 and %egfr.value.value < 45',
  g4: '%egfr.value.value >= 15 and %egfr.value.value < 30',
  g5: '%egfr.value.value < 15',
} as const;

/** The UACR bounds of each albuminuria category, in mg/g. */
const ALBUMINURIA_CATEGORY = {
  a1: '%uacr.value.value < 30',
  a2: '%uacr.value.value >= 30 and %uacr.value.value < 300',
  a3: '%uacr.value.value >= 300',
} as const;

/**
 * One cell of the grid, as a pair of actions — eGFR and UACR are monitored together at the same
 * frequency, because they are the two axes the cell is indexed on.
 *
 * The condition requires both values to exist. A chart missing either cannot be placed in the grid,
 * and falls through to the unconditional yearly floor in `ckd-ongoing-measurements` rather than being
 * guessed into a cell.
 */
const gridCell = (
  id: string,
  gfr: keyof typeof GFR_CATEGORY,
  albuminuria: keyof typeof ALBUMINURIA_CATEGORY,
  frequency: Timing,
  description: string,
): PlanDefinitionAction[] => {
  const expression = `%egfr.exists() and %uacr.exists() and (${GFR_CATEGORY[gfr]}) and (${ALBUMINURIA_CATEGORY[albuminuria]})`;
  const humanReadable = `eGFR in ${gfr.toUpperCase()} and albuminuria in ${albuminuria.toUpperCase()}`;
  const documentation = [
    { type: 'citation' as const, display: `${KDIGO_CKD_2024.title}, Figure 13`, url: KDIGO_CKD_2024.url },
  ];

  return [
    {
      id: `${id}-egfr`,
      title: 'eGFR',
      description,
      code: loincCode(LOINC_CODES.EGFR),
      participant: patient,
      extension: [itemKind('measurement'), taskCategory('measurement')],
      condition: applicable(expression, humanReadable),
      timingTiming: frequency,
      documentation,
    },
    {
      id: `${id}-uacr`,
      title: 'Urine albumin-creatinine ratio',
      description,
      code: loincCode(LOINC_CODES.URINE_ALBUMIN_CREATININE_RATIO),
      participant: patient,
      extension: [itemKind('measurement'), taskCategory('measurement')],
      condition: applicable(expression, humanReadable),
      timingTiming: frequency,
      documentation,
    },
  ];
};

// --- the grid -----------------------------------------------------------------------------------------

const CELLS: PlanDefinitionAction[] = [
  ...gridCell('g1-a1', 'g1', 'a1', MONITORING_FREQUENCIES.yearly, 'eGFR and albuminuria yearly.'),
  ...gridCell('g2-a1', 'g2', 'a1', MONITORING_FREQUENCIES.yearly, 'eGFR and albuminuria yearly.'),
  ...gridCell('g1-a2', 'g1', 'a2', MONITORING_FREQUENCIES.yearly, 'eGFR and albuminuria yearly.'),
  ...gridCell('g1-a3', 'g1', 'a3', MONITORING_FREQUENCIES.thriceYearly, 'eGFR and albuminuria three times a year.'),
  ...gridCell('g2-a2', 'g2', 'a2', MONITORING_FREQUENCIES.yearly, 'eGFR and albuminuria yearly.'),
  ...gridCell('g2-a3', 'g2', 'a3', MONITORING_FREQUENCIES.thriceYearly, 'eGFR and albuminuria three times a year.'),
  ...gridCell('g3a-a1', 'g3a', 'a1', MONITORING_FREQUENCIES.yearly, 'eGFR and albuminuria yearly.'),
  ...gridCell('g3a-a2', 'g3a', 'a2', MONITORING_FREQUENCIES.twiceYearly, 'eGFR and albuminuria twice a year.'),
  ...gridCell('g3a-a3', 'g3a', 'a3', MONITORING_FREQUENCIES.thriceYearly, 'eGFR and albuminuria three times a year.'),
  ...gridCell('g3b-a1', 'g3b', 'a1', MONITORING_FREQUENCIES.twiceYearly, 'eGFR and albuminuria twice a year.'),
  ...gridCell('g3b-a2', 'g3b', 'a2', MONITORING_FREQUENCIES.thriceYearly, 'eGFR and albuminuria three times a year.'),
  ...gridCell('g3b-a3', 'g3b', 'a3', MONITORING_FREQUENCIES.thriceYearly, 'eGFR and albuminuria three times a year.'),
  ...gridCell('g4-a1', 'g4', 'a1', MONITORING_FREQUENCIES.thriceYearly, 'eGFR and albuminuria three times a year.'),
  ...gridCell('g4-a2', 'g4', 'a2', MONITORING_FREQUENCIES.thriceYearly, 'eGFR and albuminuria three times a year.'),
  ...gridCell('g4-a3', 'g4', 'a3', MONITORING_FREQUENCIES.fourTimesYearly, 'eGFR and albuminuria four times a year.'),
  ...gridCell('g5-a1', 'g5', 'a1', MONITORING_FREQUENCIES.fourTimesYearly, 'eGFR and albuminuria four times a year.'),
  ...gridCell('g5-a2', 'g5', 'a2', MONITORING_FREQUENCIES.fourTimesYearly, 'eGFR and albuminuria four times a year.'),
  ...gridCell('g5-a3', 'g5', 'a3', MONITORING_FREQUENCIES.fourTimesYearly, 'eGFR and albuminuria four times a year.'),
];

// --- the protocol -------------------------------------------------------------------------------------

/**
 * KDIGO's stage-indexed monitoring frequency for CKD, as conditional actions on the patient's own
 * latest eGFR and albuminuria.
 *
 * This is a companion to `ckd-ongoing-measurements`, not a replacement. That protocol carries the
 * unconditional yearly floor every CKD chart gets; this one raises the frequency for the charts whose
 * numbers place them in a cell asking for more. A chart with no eGFR or no UACR on file matches no
 * cell here and keeps the floor, which is the right answer — a patient with no measurements cannot be
 * staged, and guessing a cell for them would either over- or under-monitor on no evidence.
 *
 * The two protocols can therefore both apply to one chart. Deduplication by result code is what stops
 * that producing two eGFR items, and the more frequent cadence is the one a clinician should see; that
 * behaviour lives in the expansion path rather than here.
 */
export const ckdStageMonitoring: PlanDefinition = {
  resourceType: 'PlanDefinition',
  url: CKD_STAGE_MONITORING_URL,
  version: CKD_STAGE_MONITORING_VERSION,
  identifier: [{ system: 'urn:ietf:rfc:3986', value: CKD_STAGE_MONITORING_URL }],
  name: 'ckd-stage-monitoring',
  title: 'Chronic kidney disease — stage-indexed monitoring',
  extension: delivery('suggest'),
  status: 'active',
  experimental: true,
  type: {
    coding: [
      {
        system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type',
        code: 'clinical-protocol',
        display: 'Clinical Protocol',
      },
    ],
  },
  date: '2026-09-15',
  publisher: 'Liza Health',
  description:
    'Monitoring frequency for chronic kidney disease indexed on GFR and albuminuria category, per KDIGO Figure 13.',
  useContext: [
    {
      code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'focus' },
      valueCodeableConcept: { coding: [...CKD_FOCUS_CODINGS] },
    },
  ],
  relatedArtifact: [{ type: 'derived-from', display: KDIGO_CKD_2024.title, url: KDIGO_CKD_2024.url }],
  action: [
    {
      id: 'stage-monitoring',
      title: 'Stage-indexed monitoring',
      description: 'How often eGFR and albuminuria are measured, by GFR and albuminuria category.',
      action: CELLS,
    },
  ],
};
